import React, { useState, useEffect } from 'react';
// Simple equirectangular projection
const mapW = 600, mapH = 300;
const toX = (lon) => ((lon + 180) / 360) * mapW;
const toY = (lat) => ((90 - lat) / 180) * mapH;

export default function ISSGroundTrack({ insights }) {
  const [pos, setPos] = useState(null);
  const [trail, setTrail] = useState([]);

  // Find ISS position from insights
  const issInsight = insights?.find(i =>
    i.sources?.includes('ISS Tracker') && i.data_points?.latitude !== undefined
  );

  useEffect(() => {
    if (!issInsight) return;
    const dp = issInsight.data_points || {};
    const lat = dp.latitude;
    const lon = dp.longitude;
    if (lat !== undefined && lon !== undefined) {
      const newPos = { lat, lon };
      setPos(newPos);
      setTrail(prev => {
        const next = [...prev, newPos];
        return next.slice(-30); // Keep last 30 positions
      });
    }
  }, [issInsight?.id, issInsight?.data_points?.latitude, issInsight?.data_points?.longitude]);

  const offline = !issInsight || issInsight.is_offline;
  const dp = issInsight?.data_points || {};

  return (
    <div className="orion-iss-track" data-testid="iss-ground-track">
      <div className="orion-gauge-header">
        <span className="orion-gauge-title">ISS GROUND TRACK</span>
        {offline && <span className="orion-stale-badge">OFFLINE</span>}
      </div>

      <svg viewBox={`0 0 ${mapW} ${mapH}`} className="orion-iss-map" data-testid="iss-map-svg">
        {/* Dark world outline */}
        <rect x="0" y="0" width={mapW} height={mapH} fill="#080808" />

        {/* Simplified continent outlines */}
        <ellipse cx={140} cy={110} rx={55} ry={35} fill="none" stroke="#1a1a1a" strokeWidth="0.5" />
        <ellipse cx={310} cy={100} rx={30} ry={50} fill="none" stroke="#1a1a1a" strokeWidth="0.5" />
        <ellipse cx={310} cy={190} rx={20} ry={30} fill="none" stroke="#1a1a1a" strokeWidth="0.5" />
        <ellipse cx={430} cy={120} rx={70} ry={40} fill="none" stroke="#1a1a1a" strokeWidth="0.5" />
        <ellipse cx={500} cy={200} rx={30} ry={20} fill="none" stroke="#1a1a1a" strokeWidth="0.5" />
        <ellipse cx={150} cy={200} rx={25} ry={40} fill="none" stroke="#1a1a1a" strokeWidth="0.5" />

        {/* Grid lines */}
        <line x1={0} y1={mapH/2} x2={mapW} y2={mapH/2} stroke="#111" strokeWidth="0.3" />
        <line x1={mapW/2} y1={0} x2={mapW/2} y2={mapH} stroke="#111" strokeWidth="0.3" />

        {/* Trail */}
        {trail.length > 1 && trail.map((p, i) => {
          if (i === 0) return null;
          const prev = trail[i - 1];
          // Don't draw across the date line
          if (Math.abs(p.lon - prev.lon) > 180) return null;
          return (
            <line key={i}
              x1={toX(prev.lon)} y1={toY(prev.lat)}
              x2={toX(p.lon)} y2={toY(p.lat)}
              stroke="#00E5FF" strokeWidth="1" opacity={0.2 + (i / trail.length) * 0.5}
            />
          );
        })}

        {/* ISS marker */}
        {pos && (
          <g>
            <circle cx={toX(pos.lon)} cy={toY(pos.lat)} r="8" fill="#00E5FF" opacity="0.15" />
            <circle cx={toX(pos.lon)} cy={toY(pos.lat)} r="4" fill="#00E5FF" opacity="0.3" />
            <circle cx={toX(pos.lon)} cy={toY(pos.lat)} r="2" fill="#00E5FF" data-testid="iss-marker" />
            <text x={toX(pos.lon) + 8} y={toY(pos.lat) - 4} fill="#00E5FF" fontSize="8" fontFamily="JetBrains Mono, monospace">ISS</text>
          </g>
        )}
      </svg>

      <div className="orion-iss-info">
        <span className="orion-iss-coord" data-testid="iss-lat">{pos ? `${pos.lat.toFixed(2)}N` : '—'}</span>
        <span className="orion-iss-coord" data-testid="iss-lon">{pos ? `${pos.lon.toFixed(2)}E` : '—'}</span>
        {dp.altitude_km && <span className="orion-iss-coord">{dp.altitude_km}km</span>}
        {dp.velocity_km_s && <span className="orion-iss-coord">{dp.velocity_km_s} km/s</span>}
      </div>
    </div>
  );
}
